import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Filter, Wallet, PieChart, Repeat, ChevronDown, ChevronUp, Download, HelpCircle, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateGroup(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(amount, currency = 'USD') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const num = Number(amount);
  if (num === 0) return currency && currency !== 'USD' ? `0.00 ${currency}` : '0.00';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  } catch {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
    return currency ? `${formatted} ${currency}` : formatted;
  }
}

function formatCryptoQuantity(amount, symbol) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const num = Number(amount);
  if (num === 0) return symbol ? `0 ${symbol}` : '0';
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 6
  }).format(num);
  return symbol ? `${formatted} ${symbol}` : formatted;
}

const CATEGORY_OPTIONS = [
  { value: 'fund', label: 'Account' },
  { value: 'portfolio', label: 'Portfolio' },
  { value: 'sip', label: 'SIP' }
];

const TIME_PRESETS = [
  { label: '7 days', getRange: () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);
    return { dateFrom: from.toISOString().slice(0, 10), dateTo: to.toISOString().slice(0, 10) };
  }},
  { label: '30 days', getRange: () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return { dateFrom: from.toISOString().slice(0, 10), dateTo: to.toISOString().slice(0, 10) };
  }},
  { label: '90 days', getRange: () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);
    return { dateFrom: from.toISOString().slice(0, 10), dateTo: to.toISOString().slice(0, 10) };
  }},
  { label: 'All time', getRange: () => ({ dateFrom: '', dateTo: '' }) }
];

/**
 * Summary = cash in/out of your AB Nexus account only (no double-counting).
 * - Account: DEPOSIT = inflow, WITHDRAWAL = outflow. Amount is positive in data; we use type for direction.
 * - Portfolio: excluded from summary. Movements are internal (same cash as Account deposit → allocation);
 *   including them would double-count with Account.
 * - SIP from global (id starts with "global_sip_"): excluded — the same cash is recorded in intra_sip Invest.
 * - SIP Create/Update: not cash → exclude.
 * - SIP Invest (intra_sip): inflow = amountIn (cash in), outflow = feePaid only.
 * Net = Inflows − Outflows (with rounding-safe sum).
 */
function getAmountForSummary(t) {
  const amount = Number(t.amount) || 0;
  const typeUpper = (t.type || t.typeLabel || '').toUpperCase();

  if (t.category === 'fund') {
    if (typeUpper === 'DEPOSIT') return { in: amount, out: 0 };
    if (typeUpper === 'WITHDRAWAL') return { in: 0, out: amount };
    return { in: amount >= 0 ? amount : 0, out: amount < 0 ? Math.abs(amount) : 0 };
  }

  if (t.category === 'portfolio') {
    return { in: 0, out: 0 };
  }

  if (t.category === 'sip') {
    if (t.id && String(t.id).startsWith('global_sip_')) return { in: 0, out: 0 };
    if (t.typeLabel === 'Create' || t.typeLabel === 'Update') return { in: 0, out: 0 };
    if (t.amountIn > 0 || t.amountOut > 0 || t.amountOutUsd != null) {
      const inVal = Number(t.amountIn) || 0;
      const feeVal = Number(t.feePaid) || 0;
      return { in: inVal, out: feeVal };
    }
    return { in: 0, out: 0 };
  }

  return { in: amount >= 0 ? amount : 0, out: amount < 0 ? Math.abs(amount) : 0 };
}

export default function TransactionHistory({ clientId, isAdminPreview } = {}) {
  const [allTransactions, setAllTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(['fund', 'portfolio', 'sip']);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [assetMenuOpen, setAssetMenuOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const filterBarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setCategoryMenuOpen(false);
        setTypeMenuOpen(false);
        setAssetMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = isAdminPreview && clientId
        ? `${API_URL}/api/admin/view-as/${clientId}/transactions`
        : `${API_URL}/api/client/transactions`;
      const token = isAdminPreview ? localStorage.getItem('adminToken') : localStorage.getItem('token');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error(res.statusText || 'Failed to load transactions');
      const data = await res.json();
      setAllTransactions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Something went wrong');
      setAllTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [isAdminPreview, clientId]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  // Build type and asset options from ALL transactions (not filtered)
  const typeOptions = useMemo(() => {
    const values = new Set();
    allTransactions.forEach((t) => {
      const label = (t.typeLabel || t.type || '').trim();
      if (label) values.add(label);
    });
    return Array.from(values).sort();
  }, [allTransactions]);

  const assetOptions = useMemo(() => {
    const values = new Set();
    allTransactions.forEach((t) => {
      let sym =
        t.category === 'sip' && t.tokenOutSymbol
          ? String(t.tokenOutSymbol).toUpperCase().trim()
          : (t.denomAsset || t.tokenOutSymbol || '').toString().toUpperCase().trim();
      // Merge USDT and USDC into USD
      if (sym === 'USDT' || sym === 'USDC') sym = 'USD';
      if (sym) values.add(sym);
    });
    return Array.from(values).sort();
  }, [allTransactions]);

  // Apply filters client-side
  const transactions = useMemo(() => {
    let filtered = [...allTransactions];

    // Category filter
    if (selectedCategories.length > 0 && selectedCategories.length < CATEGORY_OPTIONS.length) {
      filtered = filtered.filter(t => selectedCategories.includes(t.category));
    }

    // Type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(t => {
        const label = (t.typeLabel || t.type || '').trim();
        return selectedTypes.includes(label);
      });
    }

    // Asset filter
    if (selectedAssets.length > 0) {
      filtered = filtered.filter(t => {
        let sym =
          t.category === 'sip' && t.tokenOutSymbol
            ? String(t.tokenOutSymbol).toUpperCase().trim()
            : (t.denomAsset || t.tokenOutSymbol || '').toString().toUpperCase().trim();
        
        // Check direct match
        if (selectedAssets.includes(sym)) return true;
        
        // If USD is selected, also match USDT and USDC
        if (selectedAssets.includes('USD') && (sym === 'USDT' || sym === 'USDC')) return true;
        
        return false;
      });
    }

    // Date filters
    if (dateFrom) {
      const from = new Date(dateFrom);
      filtered = filtered.filter(t => t.dateSort && new Date(t.dateSort) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t => t.dateSort && new Date(t.dateSort) <= to);
    }

    return filtered;
  }, [allTransactions, selectedCategories, selectedTypes, selectedAssets, dateFrom, dateTo]);

  const summary = useMemo(() => {
    let inTotal = 0, outTotal = 0;
    transactions.forEach((t) => {
      const { in: i, out: o } = getAmountForSummary(t);
      inTotal += i;
      outTotal += o;
    });
    const net = Math.round((inTotal - outTotal) * 100) / 100;
    return { inTotal, outTotal, net, count: transactions.length };
  }, [transactions]);

  const groupedByDate = useMemo(() => {
    const map = new Map();
    transactions.forEach((t) => {
      const key = t.dateSort ? t.dateSort.slice(0, 10) : 'unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    // Within each date: show deposits first, then withdrawals (other types keep order)
    const depositOrder = (t) => {
      const typeUpper = (t.type || t.typeLabel || '').toString().toUpperCase();
      if (typeUpper === 'DEPOSIT') return 0;
      if (typeUpper === 'WITHDRAWAL') return 1;
      return 0.5;
    };
    map.forEach((arr) => {
      arr.sort((a, b) => depositOrder(a) - depositOrder(b));
    });
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [transactions]);

  const categoryLabel = useMemo(() => {
    if (!selectedCategories.length || selectedCategories.length === CATEGORY_OPTIONS.length) return 'All';
    return CATEGORY_OPTIONS
      .filter((o) => selectedCategories.includes(o.value))
      .map((o) => o.label)
      .join(', ');
  }, [selectedCategories]);

  const typeLabel = useMemo(() => {
    if (!selectedTypes.length) return 'All';
    return selectedTypes.join(', ');
  }, [selectedTypes]);

  const assetLabel = useMemo(() => {
    if (!selectedAssets.length) return 'All';
    return selectedAssets.join(', ');
  }, [selectedAssets]);

  const getCategoryDisplayLabel = (categoryLabel, category) => {
    // Map backend labels to frontend display labels
    if (categoryLabel === 'Fund' || category === 'fund') return 'Account';
    if (categoryLabel === 'Portfolio' || category === 'portfolio') return 'Portfolio';
    if (categoryLabel === 'SIP' || category === 'sip') return 'SIP';
    return categoryLabel || category;
  };

  const categoryIcon = (cat) => {
    switch (cat) {
      case 'fund': return <Wallet className="h-4 w-4 text-[#676DFF]" />;
      case 'portfolio': return <PieChart className="h-4 w-4 text-emerald-400" />;
      case 'sip': return <Repeat className="h-4 w-4 text-amber-400" />;
      default: return <Wallet className="h-4 w-4 text-[#B8B8B8]" />;
    }
  };

  const renderAmount = (t) => {
    if (t.category === 'sip' && (t.sipAmount != null || t.sipDuration) && (t.typeLabel === 'Create' || t.typeLabel === 'Update')) {
      return (
        <span className="text-sm">
          <span className="text-emerald-400">+{formatAmount(t.sipAmount != null ? t.sipAmount : 0, 'USD')}</span>
          {t.sipDuration && <span className="block text-xs text-[#808080]">{t.sipDuration}</span>}
        </span>
      );
    }
    if (t.category === 'sip' && (t.amountIn > 0 || t.amountOut > 0 || t.amountOutUsd != null)) {
      return (
        <span className="text-sm">
          {(t.amountOutUsd != null || t.amountOut > 0) && (
            <span className="text-emerald-400">
              +{t.amountOutUsd != null ? formatAmount(t.amountOutUsd)
                : (t.amountOutHuman != null && t.tokenOutSymbol
                  ? (['BTC', 'ETH'].includes(String(t.tokenOutSymbol).toUpperCase())
                    ? formatCryptoQuantity(t.amountOutHuman, t.tokenOutSymbol)
                    : formatAmount(t.amountOutHuman, t.tokenOutSymbol))
                  : (t.amountOut > 1e9 ? '—' : formatAmount(t.amountOut)))}
            </span>
          )}
          {t.feePaid > 0 && <span className="block text-xs text-red-400">Fee: {formatAmount(t.feePaid)}</span>}
        </span>
      );
    }
    const amt = Number(t.amount) || 0;
    // Fund/Portfolio: amount is always positive; use type for direction. SIP/other: use sign of amount.
    const typeUpper = (t.type || t.typeLabel || '').toString().toUpperCase().replace(/\s+/g, ' ');
    const isWithdrawal = typeUpper === 'WITHDRAWAL';
    const isFeeOut = typeUpper === 'FEE_OUT' || typeUpper === 'FEE PAID' || typeUpper === 'FEE OUT';
    const isOutflow = isWithdrawal || isFeeOut;
    const isIn = (t.category === 'fund' || t.category === 'portfolio') ? !isOutflow : amt >= 0;
    return (
      <span className={`text-sm font-medium ${isIn ? 'text-emerald-400' : 'text-red-400'}`}>
        {isIn ? '+' : ''}{formatAmount(amt, 'USD')}
      </span>
    );
  };

  const renderDenomAsset = (t) => {
    if (t.denomAssetAmount != null && t.denomAsset) {
      return ['BTC', 'ETH'].includes(String(t.denomAsset).toUpperCase())
        ? formatCryptoQuantity(t.denomAssetAmount, t.denomAsset)
        : formatAmount(t.denomAssetAmount, t.denomAsset);
    }
    if (t.category === 'sip' && t.tokenOutSymbol && t.amountOutHuman != null) {
      return ['BTC', 'ETH'].includes(String(t.tokenOutSymbol).toUpperCase())
        ? formatCryptoQuantity(t.amountOutHuman, t.tokenOutSymbol)
        : formatAmount(t.amountOutHuman, t.tokenOutSymbol);
    }
    if (t.category === 'sip' && (t.typeLabel === 'Create' || t.typeLabel === 'Update') && t.denomAsset) return t.denomAsset;
    return '—';
  };

  const getDisplayPrice = (t) => {
    // For Account/Portfolio, use denomAssetPrice directly
    if (t.category !== 'sip' && t.denomAssetPrice != null) return Number(t.denomAssetPrice);

    // For SIP, only show price for Invest (use tokenOutPrice); ignore global SIP denomAssetPrice
    if (t.category === 'sip') {
      const isInvest =
        t.typeLabel === 'Invest' ||
        (t.type && String(t.type).toUpperCase() === 'INVEST');
      if (isInvest && t.tokenOutPrice != null) {
        return Number(t.tokenOutPrice);
      }
    }
    return null;
  };

  const exportCSV = useCallback(() => {
    const headers = ['Date', 'Category', 'Type', 'Amount (USD)', 'Asset', 'Quantity', 'Price', 'NAV', 'Product'];
    
    const getAssetData = (t) => {
      let asset = '';
      let quantity = '';
      let price = '';

      // Get asset symbol
      if (t.category === 'sip' && t.tokenOutSymbol) {
        asset = String(t.tokenOutSymbol).toUpperCase();
      } else if (t.denomAsset) {
        asset = String(t.denomAsset).toUpperCase();
      }

      // Get quantity
      if (t.denomAssetAmount != null) {
        quantity = Number(t.denomAssetAmount);
      } else if (t.category === 'sip' && t.amountOutHuman != null) {
        quantity = Number(t.amountOutHuman);
      }

      // Get price
      const priceValue = getDisplayPrice(t);
      if (priceValue != null) {
        price = Number(priceValue);
      }

      return { asset, quantity, price };
    };

    const rows = transactions.map((t) => {
      const { asset, quantity, price } = getAssetData(t);
      return [
        formatDate(t.date),
        getCategoryDisplayLabel(t.categoryLabel, t.category),
        t.typeLabel || t.type || '—',
        (() => {
          if (t.category === 'sip' && (t.amountOutUsd != null || t.amountOut > 0)) {
            return t.amountOutUsd != null ? t.amountOutUsd.toFixed(2) : (t.amountOut > 1e9 ? '' : t.amountOut);
          }
          return typeof t.amount === 'number' ? t.amount.toFixed(2) : (t.amountIn ?? t.amount ?? '');
        })(),
        asset,
        quantity,
        price,
        t.nav != null ? Number(t.nav) : '',
        t.category === 'sip' ? 'SIP' : (t.referenceProductLabel || t.fromPortfolioLabel || t.toPortfolioLabel || '')
      ];
    });
    const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [transactions]);

  const TransactionRow = ({ t }) => (
    <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
      <td className="px-4 py-3 text-white text-sm whitespace-nowrap">{formatDate(t.date)}</td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-sm text-[#E0E0E0]">
          {categoryIcon(t.category)}
          {getCategoryDisplayLabel(t.categoryLabel, t.category)}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-[#E0E0E0]">{t.typeLabel || t.type || '—'}</td>
      <td className="px-4 py-3 text-right">{renderAmount(t)}</td>
      <td className="px-4 py-3 text-[#B8B8B8] text-sm">
        <span>{renderDenomAsset(t)}</span>
        {(() => {
          const price = getDisplayPrice(t);
          return price != null ? (
            <span className="block text-xs text-[#808080] mt-0.5">
              @ {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(price)}
            </span>
          ) : null;
        })()}
      </td>
      <td className="px-4 py-3 text-right text-[#B8B8B8] text-sm">
        {t.nav != null ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(Number(t.nav)) : '—'}
      </td>
      <td className="px-4 py-3 text-[#B8B8B8] text-sm">{t.category === 'sip' ? 'SIP' : (t.referenceProductLabel || t.fromPortfolioLabel || t.toPortfolioLabel || '—')}</td>
    </tr>
  );

  const TransactionCard = ({ t }) => (
    <div key={t.id} className="p-4 rounded-lg bg-white/[0.03] border border-white/10 hover:border-white/20 transition-colors">
      <div className="flex justify-between items-start gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {categoryIcon(t.category)}
          <div>
            <p className="text-sm font-medium text-white">{t.typeLabel || t.type || '—'}</p>
            <p className="text-xs text-[#808080]">{getCategoryDisplayLabel(t.categoryLabel, t.category)} · {t.category === 'sip' ? 'SIP' : (t.referenceProductLabel || t.fromPortfolioLabel || '—')}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">{renderAmount(t)}</div>
      </div>
      <div className="mt-2 flex justify-between text-xs text-[#808080]">
        <span>{formatDate(t.date)}</span>
        <span>
          {renderDenomAsset(t)}
          {(() => {
            const price = getDisplayPrice(t);
            return price != null ? (
              <span className="block text-[10px] opacity-80">
                @ {new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(price)}
              </span>
            ) : null;
          })()}
        </span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" style={{ fontFamily: 'Lexend, sans-serif' }}>
      {/* Header + filters + summary (sticky) */}
      <div className="sticky top-0 z-10 flex flex-col gap-4 pb-4 bg-[#0A0A0A]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 overflow-x-auto pb-1 md:pb-0">
            {TIME_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const { dateFrom: from, dateTo: to } = preset.getRange();
                  setDateFrom(from);
                  setDateTo(to);
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-white/20 text-[#B8B8B8] hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap flex-shrink-0"
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              onClick={exportCSV}
              disabled={transactions.length === 0}
              title="Export CSV"
              className="p-2 rounded-lg border border-[#676DFF]/40 text-[#676DFF] hover:bg-[#676DFF]/10 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Mobile: collapsible filters trigger */}
        <div className="md:hidden">
          <button
            type="button"
            onClick={() => setFiltersExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#151515] border border-white/10 text-left text-sm text-[#E0E0E0] hover:bg-[#1a1a1a]"
          >
            <span className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#808080]" />
              Filters
              {(categoryLabel !== 'All' || typeLabel !== 'All' || assetLabel !== 'All' || dateFrom || dateTo) && (
                <span className="text-xs text-[#676DFF] font-medium">(active)</span>
              )}
            </span>
            {filtersExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {/* Filter bar: always visible on md+, collapsible on mobile */}
        <div
          ref={filterBarRef}
          className={`flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 p-3 rounded-xl bg-[#151515] border border-white/10 ${filtersExpanded ? 'block' : 'hidden md:flex'}`}
        >
          <span className="flex items-center gap-2 text-[#808080] text-sm">
            <Filter className="h-4 w-4" /> Filters
          </span>

          {/* Category dropdown with checkboxes */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setCategoryMenuOpen((v) => !v);
                setTypeMenuOpen(false);
                setAssetMenuOpen(false);
              }}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-lg bg-[#0A0A0A] border border-white/20 text-[#E0E0E0]"
            >
              <span className="text-[#808080] uppercase tracking-wide">Category:</span>
              <span className="truncate max-w-[120px]">{categoryLabel}</span>
              <ChevronDown className="h-3 w-3 text-[#808080]" />
            </button>
            {categoryMenuOpen && (
              <div className="absolute z-20 mt-1 w-48 rounded-lg bg-[#0A0A0A] border border-white/15 shadow-lg p-2 space-y-1">
                <label className="flex items-center gap-2 text-xs text-[#E0E0E0] mb-1 border-b border-white/10 pb-1">
                  <input
                    type="checkbox"
                    className="h-3 w-3 rounded border-[#555] bg-[#0A0A0A]"
                    checked={selectedCategories.length === CATEGORY_OPTIONS.length}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedCategories(checked ? CATEGORY_OPTIONS.map((o) => o.value) : []);
                    }}
                  />
                  Select all
                </label>
                {CATEGORY_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 text-xs text-[#E0E0E0]">
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded border-[#555] bg-[#0A0A0A]"
                      checked={selectedCategories.includes(opt.value)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedCategories((prev) => {
                          if (checked) return Array.from(new Set([...prev, opt.value]));
                          const next = prev.filter((v) => v !== opt.value);
                          return next.length ? next : CATEGORY_OPTIONS.map((o) => o.value);
                        });
                      }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Type dropdown with checkboxes */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setTypeMenuOpen((v) => !v);
                setCategoryMenuOpen(false);
                setAssetMenuOpen(false);
              }}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-lg bg-[#0A0A0A] border border-white/20 text-[#E0E0E0]"
            >
              <span className="text-[#808080] uppercase tracking-wide">Type:</span>
              <span className="truncate max-w-[140px]">{typeLabel}</span>
              <ChevronDown className="h-3 w-3 text-[#808080]" />
            </button>
            {typeMenuOpen && typeOptions.length > 0 && (
              <div className="absolute z-20 mt-1 w-52 max-h-56 overflow-auto rounded-lg bg-[#0A0A0A] border border-white/15 shadow-lg p-2 space-y-1">
                <label className="flex items-center gap-2 text-xs text-[#E0E0E0] mb-1 border-b border-white/10 pb-1">
                  <input
                    type="checkbox"
                    className="h-3 w-3 rounded border-[#555] bg-[#0A0A0A]"
                    checked={selectedTypes.length === typeOptions.length}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedTypes(checked ? [...typeOptions] : []);
                    }}
                  />
                  Select all
                </label>
                {typeOptions.map((label) => (
                  <label key={label} className="flex items-center gap-2 text-xs text-[#E0E0E0]">
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded border-[#555] bg-[#0A0A0A]"
                      checked={selectedTypes.includes(label)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedTypes((prev) =>
                          checked
                            ? Array.from(new Set([...prev, label]))
                            : prev.filter((v) => v !== label)
                        );
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Asset dropdown with checkboxes */}
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setAssetMenuOpen((v) => !v);
                setCategoryMenuOpen(false);
                setTypeMenuOpen(false);
              }}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-lg bg-[#0A0A0A] border border-white/20 text-[#E0E0E0]"
            >
              <span className="text-[#808080] uppercase tracking-wide">Asset:</span>
              <span className="truncate max-w-[100px]">{assetLabel}</span>
              <ChevronDown className="h-3 w-3 text-[#808080]" />
            </button>
            {assetMenuOpen && assetOptions.length > 0 && (
              <div className="absolute z-20 mt-1 w-40 max-h-56 overflow-auto rounded-lg bg-[#0A0A0A] border border-white/15 shadow-lg p-2 space-y-1">
                <label className="flex items-center gap-2 text-xs text-[#E0E0E0] mb-1 border-b border-white/10 pb-1">
                  <input
                    type="checkbox"
                    className="h-3 w-3 rounded border-[#555] bg-[#0A0A0A]"
                    checked={selectedAssets.length === assetOptions.length}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedAssets(checked ? [...assetOptions] : []);
                    }}
                  />
                  Select all
                </label>
                {assetOptions.map((sym) => (
                  <label key={sym} className="flex items-center gap-2 text-xs text-[#E0E0E0]">
                    <input
                      type="checkbox"
                      className="h-3 w-3 rounded border-[#555] bg-[#0A0A0A]"
                      checked={selectedAssets.includes(sym)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedAssets((prev) =>
                          checked
                            ? Array.from(new Set([...prev, sym]))
                            : prev.filter((v) => v !== sym)
                        );
                      }}
                    />
                    {sym}
                  </label>
                ))}
              </div>
            )}
          </div>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-[#0A0A0A] border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#676DFF]/50 outline-none w-full md:w-auto min-w-0"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-[#0A0A0A] border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#676DFF]/50 outline-none w-full md:w-auto min-w-0"
          />
        </div>

        {/* Summary strip: one row, Transactions smaller */}
        {!loading && transactions.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="p-2.5 sm:p-3 rounded-xl bg-[#151515] border border-white/10 min-w-0">
              <p className="text-[10px] text-[#808080] uppercase tracking-wider">Transactions</p>
              <p className="text-sm font-semibold text-white mt-0.5">{summary.count}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 min-w-0">
              <p className="text-[10px] sm:text-xs text-emerald-400/80 uppercase tracking-wider flex items-center gap-1"><ArrowDownCircle className="h-3 w-3 flex-shrink-0" /> Inflows</p>
              <p className="text-base sm:text-lg font-semibold text-emerald-400 mt-0.5 truncate" title={formatAmount(summary.inTotal)}>{formatAmount(summary.inTotal)}</p>
            </div>
            <div className="p-3 sm:p-4 rounded-xl bg-red-500/10 border border-red-500/20 min-w-0">
              <p className="text-[10px] sm:text-xs text-red-400/80 uppercase tracking-wider flex items-center gap-1"><ArrowUpCircle className="h-3 w-3 flex-shrink-0" /> Outflows</p>
              <p className="text-base sm:text-lg font-semibold text-red-400 mt-0.5 truncate" title={formatAmount(summary.outTotal)}>{formatAmount(summary.outTotal)}</p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl px-4 py-3 text-sm">{error}</div>
      )}

      {/* Help expander */}
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left text-sm text-[#B8B8B8] hover:bg-white/[0.02] transition-colors"
        >
          <span className="flex items-center gap-2"><HelpCircle className="h-4 w-4" /> What do Account, Portfolio, and SIP mean?</span>
          {showHelp ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showHelp && (
          <div className="px-4 pb-4 pt-0 text-sm text-[#808080] border-t border-white/5 space-y-3">
            <p><strong className="text-[#B8B8B8]">Account</strong> — Cash going into or out of your AB Nexus account. Think of it as your main wallet: when you add or withdraw money, it shows here.</p>
            <p><strong className="text-[#B8B8B8]">Portfolio</strong> — Moves and changes inside your invested strategies: moving money between portfolios, fees, referrals, and deposits or withdrawals within a strategy.</p>
            <p><strong className="text-[#B8B8B8]">SIP</strong> — Your recurring investment plans. This includes when you set up or change a plan, and each time a scheduled buy or withdrawal runs.</p>
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-[#151515] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-sm text-[#808080]">Loading your transactions…</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-20 px-4">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Wallet className="h-8 w-8 text-[#606060]" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No transactions in this period</h3>
            <p className="text-sm text-[#808080] max-w-sm mx-auto mb-4">
              Try a different date range or category. Account and portfolio activity will appear here once you have deposits, withdrawals, or movements.
            </p>
            <button
              type="button"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setSelectedCategories(CATEGORY_OPTIONS.map((o) => o.value));
                setSelectedTypes([]);
                setSelectedAssets([]);
              }}
              className="text-sm text-[#676DFF] hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            {/* Desktop: table grouped by date */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10 text-[#808080] text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 font-semibold">Date</th>
                    <th className="px-4 py-3 font-semibold">Source</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold">Asset / quantity</th>
                    <th className="px-4 py-3 font-semibold text-right">NAV</th>
                    <th className="px-4 py-3 font-semibold">Product</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedByDate.map(([dateKey, list]) => (
                    <React.Fragment key={dateKey}>
                      <tr className="bg-white/[0.03]">
                        <td colSpan={7} className="px-4 py-2 text-xs font-semibold text-[#808080] uppercase tracking-wider">
                          {formatDateGroup(dateKey)}
                        </td>
                      </tr>
                      {list.map((t) => (
                        <TransactionRow key={t.id} t={t} />
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards grouped by date */}
            <div className="lg:hidden divide-y divide-white/5">
              {groupedByDate.map(([dateKey, list]) => (
                <div key={dateKey} className="p-4">
                  <p className="text-xs font-semibold text-[#808080] uppercase tracking-wider mb-3">{formatDateGroup(dateKey)}</p>
                  <div className="space-y-3">
                    {list.map((t) => (
                      <TransactionCard key={t.id} t={t} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
